// Loads catalog.json + applies enrichments (anchors, variant grouping).
// One source of truth for /books, /listen, /shop, /watch.
//
// catalog.json is a build-time snapshot of the Supabase `products` and `songs`
// tables, written by scripts/fetch-catalog.py. The site is static and reads no
// database at runtime — which is also why no credential ever reaches the
// client. Re-run the script after any catalogue edit.

import raw from "@/data/catalog.json";
import {
  bookAnchors,
  bookSamplePanels,
  songVariantGroups,
  songAnchors,
} from "@/data/enrichments";

// ---- Books -----------------------------------------------------------------

export function getBooks() {
  return raw.books.map((b) => ({
    ...b,
    anchor: bookAnchors[b.slug] || null,
    sample_panels: bookSamplePanels[b.slug] || [],
    is_live: b.status === "published",
    is_pending: b.status === "pending_launch",
  }));
}

export function getBook(slug) {
  return getAllBooks().find((b) => b.slug === slug);
}

// ---- Books · prose ---------------------------------------------------------

// Standalone prose titles (products.type = 'book'). Not graphic novels, but a
// published book the catalogue hid until now is still a published book.
export function getProse() {
  return (raw.prose || []).map((b) => ({
    ...b,
    anchor: bookAnchors[b.slug] || null,
    sample_panels: [],
    is_live: b.status === "published",
    is_pending: b.status === "pending_launch",
    kind: "book",
  }));
}

// Everything on /books: graphic novels first (the studio's spine), then prose.
export function getAllBooks() {
  return [...getBooks().map((b) => ({ ...b, kind: b.kind || "comic" })), ...getProse()];
}

// ---- Films + app -----------------------------------------------------------

export function getFilms() {
  return raw.films || [];
}

export function getApp() {
  return (raw.apps || [])[0] || null;
}

// Books visible on /books — published + pending_launch (with badge).
// pending_launch books still surface (so Hanuman Chalisa appears today).
export function getVisibleBooks() {
  return getBooks().filter(
    (b) => b.status === "published" || b.status === "pending_launch"
  );
}

// Every book the shop window should show — graphic novels and prose alike.
export function getVisibleAllBooks() {
  return getAllBooks().filter(
    (b) => b.status === "published" || b.status === "pending_launch"
  );
}

// ---- Books · storefronts ---------------------------------------------------

// Turn a book's platform_ids jsonb into an ordered list of storefront buttons.
// Each entry: { id, label, url, secondary?, comingSoon? }
export function getStorefronts(book) {
  if (!book) return [];
  const ids = book.platform_ids || {};
  const out = [];

  if (ids.amazon_us) {
    out.push({
      id: "amazon-us",
      label: "Amazon · US",
      url: ids.amazon_us,
    });
  } else if (ids.kdp_paperback_asin) {
    // Prose titles carry only the ASIN. An ASIN is a canonical Amazon URL —
    // refusing to build it is how a published book ends up with no way to buy
    // it on the one page whose job is to sell it.
    out.push({
      id: "amazon-us-paperback",
      label: "Amazon · Paperback",
      url: `https://www.amazon.com/dp/${ids.kdp_paperback_asin}`,
    });
  } else if (ids.amazon_us_kindle) {
    out.push({
      id: "amazon-us-kindle",
      label: "Amazon US · Kindle",
      url: ids.amazon_us_kindle,
    });
  }

  if (ids.amazon_in) {
    out.push({ id: "amazon-in", label: "Amazon · India", url: ids.amazon_in });
  } else if (ids.amazon_in_kindle) {
    out.push({
      id: "amazon-in-kindle",
      label: "Amazon India · Kindle",
      url: ids.amazon_in_kindle,
    });
  }

  if (ids.amazon_us_paperback) {
    out.push({
      id: "amazon-us-paperback",
      label: "Amazon US · Paperback",
      url: ids.amazon_us_paperback,
      secondary: true,
    });
  }
  if (ids.amazon_in_paperback) {
    out.push({
      id: "amazon-in-paperback",
      label: "Amazon India · Paperback",
      url: ids.amazon_in_paperback,
      secondary: true,
    });
  }

  if (ids.google_play_url) {
    out.push({
      id: "google-play",
      label: "Google Play",
      url: ids.google_play_url,
    });
  } else if (ids.google_books_id) {
    out.push({
      id: "google-books",
      label: "Google Books",
      url: `https://play.google.com/store/books/details?id=${ids.google_books_id}`,
    });
  }

  // Wide-shelf storefronts (Wide Shelf plan, Sep 2026): free serialized
  // reading + direct sales sit alongside the retailers, never instead of them.
  if (ids.webtoon_url) {
    out.push({ id: "webtoon", label: "Read free on WEBTOON", url: ids.webtoon_url });
  }
  if (ids.gumroad_url) {
    out.push({ id: "gumroad", label: "Buy direct · DRM-free PDF", url: ids.gumroad_url });
  }
  if (ids.globalcomix_url) {
    out.push({ id: "globalcomix", label: "Read on GlobalComix", url: ids.globalcomix_url });
  }

  // Pending-launch hint
  if (book.is_pending && !ids.amazon_us && !ids.amazon_us_kindle) {
    out.push({
      id: "amazon-coming",
      label: "Amazon · in review",
      url: null,
      comingSoon: true,
    });
  }

  return out;
}

// The single best storefront for an index card: the first button
// getStorefronts() would render that actually has a URL.
export function primaryStorefront(book) {
  return getStorefronts(book).find((s) => s.url) || null;
}

export function priceLine(book) {
  const p = book?.price;
  if (!p) return null;
  const parts = [];
  if (p.kdp_usd) parts.push(`$${p.kdp_usd} Kindle`);
  if (p.kdp_kindle_usd) parts.push(`$${p.kdp_kindle_usd} Kindle`);
  if (p.kdp_paperback_usd) parts.push(`$${p.kdp_paperback_usd} paperback`);
  if (p.kdp_kindle_inr) parts.push(`₹${p.kdp_kindle_inr} Kindle (IN)`);
  if (p.google_books_sgd) parts.push(`SGD ${p.google_books_sgd} Play`);
  if (p.google_books_usd) parts.push(`$${p.google_books_usd} Play`);
  return parts.join(" · ");
}

// ---- Songs -----------------------------------------------------------------

const groupBySlug = (() => {
  const m = new Map();
  for (const [groupSlug, g] of Object.entries(songVariantGroups)) {
    for (const memberTitle of g.members) {
      m.set(memberTitle, { groupSlug, ...g });
    }
  }
  return m;
})();

// Returns canonical song cards: variants collapsed to one card, anchored.
// Each card = { slug, title, tradition, members[], representative, anchor }
export function getMusicCatalog() {
  const songs = raw.songs;
  const seenGroups = new Set();
  const cards = [];

  for (const s of songs) {
    const g = groupBySlug.get(s.title);
    if (g) {
      if (seenGroups.has(g.groupSlug)) continue;
      seenGroups.add(g.groupSlug);
      const members = g.members
        .map((title) => songs.find((x) => x.title === title))
        .filter(Boolean);
      const representative = members[0];
      cards.push({
        slug: g.groupSlug,
        title: g.canonical_title,
        tradition: representative.tradition,
        anchor: g.canonical_anchor || null,
        is_group: true,
        is_album: !!g.is_album,
        members_label: g.members_label || null,
        member_labels: g.member_labels
          ? members.map((m) => g.member_labels[g.members.indexOf(m.title)] || m.title)
          : null,
        intro: g.intro || null,
        cover_url: g.cover_url || null,
        album_links: g.album_links || null,
        members,
        representative,
      });
    } else {
      cards.push({
        slug: s.slug,
        title: s.title,
        tradition: s.tradition,
        anchor: songAnchors[s.slug] || null,
        is_group: false,
        members: [s],
        representative: s,
      });
    }
  }
  return cards;
}

export function getSongCard(slug) {
  return getMusicCatalog().find((c) => c.slug === slug);
}

// Pick N "featured" songs for the homepage.
export function getFeaturedSongs(n = 6) {
  const featuredOrder = [
    "hanuman-chalisa",
    "mere-giridhar-gopal-meera-bhajan",
    "heart-sutra",
    "asato-maa-sad-gamaya",
    "gate-gate-paragate",
    "birth-of-ma-tara",
  ];
  const all = getMusicCatalog();
  const out = [];
  for (const slug of featuredOrder) {
    const c = all.find((x) => x.slug === slug);
    if (c) out.push(c);
    if (out.length >= n) break;
  }
  return out;
}

// Recordings in the catalogue, before variant grouping collapses them into
// cards. The card count and the recording count are different numbers and the
// page should not quietly use one where it means the other.
export function getSongCount() {
  return (raw.songs || []).length;
}

export function formatDuration(sec) {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ---- Featured book on homepage --------------------------------------------

export function getFeaturedBook() {
  const books = getVisibleBooks();
  // Most recent published book wins.
  return (
    books.find((b) => b.status === "published") || books[0] || null
  );
}
